import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { CurrencyViewModel } from '@mehir/api/finance-api';
import { convertTLToUSD, formatTL, formatUSD } from '../modules/contracts/utils';

// Currency display mode, shared across every <MoneyAmount>: clicking any amount
// switches the whole page between TL and USD.
//
// This used to be a module-level `let` with a hand-rolled subscriber set, which
// meant the mode survived across tests and could not be inspected in DevTools.
// A context does the same job with none of that.
type Mode = 'tl' | 'usd';

interface CurrencyModeValue {
  mode: Mode;
  toggle: () => void;
}

const CurrencyModeContext = createContext<CurrencyModeValue>({
  mode: 'tl',
  toggle: () => undefined,
});

export const CurrencyModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<Mode>('tl');
  const toggle = useCallback(() => setMode((m) => (m === 'tl' ? 'usd' : 'tl')), []);
  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return <CurrencyModeContext.Provider value={value}>{children}</CurrencyModeContext.Provider>;
};

export interface MoneyAmountProps {
  /** Amount already converted to TRY. */
  tl: number | null | undefined;
  /** Live currency rates — needed to derive the USD view. */
  currencies: CurrencyViewModel[] | undefined;
  /** Prefix the value with "≈". */
  approximate?: boolean;
  /** Text rendered when `tl` is null/undefined. */
  fallback?: string;
  className?: string;
}

/**
 * Renders a TRY amount that flips to USD on click (and back). The toggle is
 * global, so clicking one amount switches every MoneyAmount on the page.
 */
export const MoneyAmount = ({
  tl,
  currencies,
  approximate,
  fallback = '-',
  className,
}: MoneyAmountProps) => {
  const { mode, toggle } = useContext(CurrencyModeContext);

  if (tl == null) return <span className={className}>{fallback}</span>;

  const usd = convertTLToUSD(tl, currencies);
  const canToggle = usd != null;
  const text =
    mode === 'usd' && usd != null
      ? formatUSD(usd, { approximate })
      : formatTL(tl, { approximate });

  const interactive = canToggle
    ? 'cursor-pointer underline decoration-dotted decoration-muted-foreground/40 underline-offset-4 transition-colors hover:decoration-current'
    : '';

  return (
    <span
      role={canToggle ? 'button' : undefined}
      tabIndex={canToggle ? 0 : undefined}
      onClick={
        canToggle
          ? (e) => {
              e.stopPropagation();
              toggle();
            }
          : undefined
      }
      onKeyDown={
        canToggle
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle();
              }
            }
          : undefined
      }
      title={
        canToggle
          ? mode === 'tl'
            ? "Dolara çevirmek için tıklayın"
            : "TL'ye çevirmek için tıklayın"
          : undefined
      }
      className={[interactive, className].filter(Boolean).join(' ')}
    >
      {text}
    </span>
  );
};
